/**
 * A tiny scoped pub/sub so components OUTSIDE the Forms tab subtree — the Diagram right panel's
 * "Manage interactions" link and its "SET UP" screen CTA — can ask the open Forms tab to focus a
 * specific step on a specific sub-tab. Mirrors `process-editor-tab-bus`: a module-local emitter
 * keeps this pure-UI navigation concern out of the Top-risk screen-design model. Only one Forms tab
 * is mounted at a time, but the listener set tolerates more.
 */

type FormsFocusListener = (stepId: string) => void;

const interactionsListeners: Set<FormsFocusListener> = new Set();
const stepListeners: Set<FormsFocusListener> = new Set();

// Retained so a request that fires *before* the Forms tab mounts (the property-panel links also
// switch the process-editor tab, which mounts the Forms tab a tick later) is not lost — the tab
// consumes it on mount. Live listeners still get notified for the already-mounted case.
let pendingInteractionsStepId: string | undefined;
let pendingStepId: string | undefined;

/** The Forms tab subscribes; returns an unsubscribe for cleanup. */
export const subscribeFormsInteractions = (listener: FormsFocusListener): (() => void) => {
  interactionsListeners.add(listener);
  return (): void => {
    interactionsListeners.delete(listener);
  };
};

/** Ask the open Forms tab to show Interactions for `stepId` (retained until consumed on mount). */
export const requestFormsInteractions = (stepId: string): void => {
  pendingInteractionsStepId = stepId;
  interactionsListeners.forEach((listener: FormsFocusListener): void => listener(stepId));
};

/** Read and clear a pending Interactions request — the Forms tab calls this on mount. */
export const consumePendingFormsInteractions = (): string | undefined => {
  const stepId: string | undefined = pendingInteractionsStepId;
  pendingInteractionsStepId = undefined;
  return stepId;
};

/** The Forms tab subscribes for Workflow Screens focus; returns an unsubscribe for cleanup. */
export const subscribeFormsStep = (listener: FormsFocusListener): (() => void) => {
  stepListeners.add(listener);
  return (): void => {
    stepListeners.delete(listener);
  };
};

/**
 * Ask the open Forms tab to select `stepId` on the Workflow Screens sub-tab (retained until
 * consumed on mount) — the Diagram property panel's "SET UP" CTA for a screenless step.
 */
export const requestFormsStep = (stepId: string): void => {
  pendingStepId = stepId;
  stepListeners.forEach((listener: FormsFocusListener): void => listener(stepId));
};

/** Read and clear a pending Workflow Screens request — the Forms tab calls this on mount. */
export const consumePendingFormsStep = (): string | undefined => {
  const stepId: string | undefined = pendingStepId;
  pendingStepId = undefined;
  return stepId;
};
